/**
 * The five sources, as a vocabulary: one colour, one icon and one title rule.
 *
 * Split out of `index.tsx` the moment a second place had to draw an item. The
 * day menu lists everything a day holds, including the entries the grid cell
 * hid behind "+3 more", so it draws the same chips the cell does - and a second
 * copy of the colour map is a copy that goes wrong the first time a sixth source
 * is added. One map, two callers.
 *
 * The colours are never shared between kinds on purpose: an invoice due is money
 * expected and a task due is be-finished, and one colour would turn five
 * different promises into one pile. See the note at the top of `index.tsx`.
 */
import { CalendarCheck2, FileText, Flag, FolderKanban, ListTodo } from "lucide-react";
import type { CalendarItem, CalendarKind } from "../../../lib/calendar";

/** The section's translator, so the two components agree on its shape. */
export type T = (key: string, vars?: Record<string, string | number>) => string;

/** One colour per source, and they are never shared. */
export const KIND_DOT: Record<CalendarKind, string> = {
  task: "bg-sky-500",
  milestone: "bg-violet-500",
  project: "bg-emerald-500",
  invoice: "bg-amber-500",
  activity: "bg-rose-500",
};

export const KIND_ICON: Record<CalendarKind, typeof ListTodo> = {
  task: ListTodo,
  milestone: Flag,
  project: FolderKanban,
  invoice: FileText,
  activity: CalendarCheck2,
};

/**
 * What an item is called on screen.
 *
 * The activity fallback title is a machine word (`type:call`) rather than a
 * sentence, because an entry with no body has nothing to show. Translated here,
 * where the vocabulary lives, so the grid chip and the menu row never disagree
 * about what an untitled next step is called.
 */
export function itemTitle(item: CalendarItem, t: T): string {
  return item.title.startsWith("type:") ? t(`activity.${item.title.slice(5)}`) : item.title;
}
